/**
 * Declaring a major incident — Step 54, the second step.
 *
 * Kai: *"declaring should be two step, and should be able to trigger a portal
 * announcement."* The second step is not ceremony. Declaring is the loudest
 * act on the desk: the reference changes on every future email, the ticket
 * grows an outage record and a review, and it can put a banner in front of
 * every customer the organisation has. One screen that says so is cheap
 * against any of that being a surprise.
 *
 * **The announcement is off by default and never automatic** (Kai: *"it
 * should never automatically do one"*, *"it should be an option with custom
 * text"*). A banner that posts itself is one nobody has read before the
 * customers do — and the first fifteen minutes of an outage is exactly when
 * you do not yet know what to tell people. The title is seeded from the
 * subject only as a starting point; an internal subject line is rarely the
 * sentence you want on a portal.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Megaphone } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Modal } from '../../components/ui/modal';
import { RichTextEditor } from '../../components/ui/richtext';
import { apiSend } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { useConfirmDiscard, useDirty } from '../../lib/dirty';

export function DeclareMajorModal({
  open,
  onClose,
  id,
  subject,
}: {
  open: boolean;
  onClose: () => void;
  id: string;
  subject: string;
}) {
  const qc = useQueryClient();
  const [announce, setAnnounce] = useState(false);
  const [title, setTitle] = useState(subject);
  const [body, setBody] = useState('');

  /**
   * The announcement wording is real free-text work — a paragraph somebody
   * chose carefully because customers read it. Registering it means closing
   * this modal mid-sentence asks first, in the same styled dialog everything
   * else uses.
   */
  const drafted =
    open && announce && (body.replace(/<[^>]+>/g, '').trim().length > 0 || title.trim() !== subject.trim());
  const clearDirty = useDirty(drafted);
  const confirmDiscard = useConfirmDiscard();
  const cancel = async () => {
    // Not `window.confirm` — one discard experience, app-wide.
    if (drafted && !(await confirmDiscard())) return;
    clearDirty();
    onClose();
  };

  const declare = useMutation({
    mutationFn: () =>
      apiSend(`/api/tickets/${id}/declare-major`, 'POST', {
        ...(announce && title.trim() ? { announce: { title: title.trim(), bodyHtml: body } } : {}),
      }),
    onSuccess: (r) => {
      if (!r.ok) return toast.error(r.error ?? 'Could not declare.');
      toast.success(announce ? 'Declared, and the portal has been told.' : 'Declared.');
      void qc.invalidateQueries({ queryKey: qk.tickets.detail(id) });
      void qc.invalidateQueries({ queryKey: qk.tickets.majorIncident(id) });
      void qc.invalidateQueries({ queryKey: qk.tickets.activity(id) });
      // The banner is live for this agent too, so the shell has to re-ask.
      void qc.invalidateQueries({ queryKey: qk.announcements.live });
      // Cleared BEFORE the close: the work is saved, so the guard must not
      // fire on the action that saved it.
      clearDirty();
      onClose();
    },
  });

  return (
    <Modal
      open={open}
      onClose={() => void cancel()}
      title="Declare a major incident"
      wide
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => void cancel()} disabled={declare.isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={declare.isPending || (announce && !title.trim())}
            onClick={() => declare.mutate()}
          >
            {declare.isPending ? 'Declaring…' : 'Declare'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* What actually changes, in plain words — the reason there is a
            second step at all. */}
        <ul className="space-y-1 text-sm text-muted-foreground">
          <li>· The reference becomes #MI, keeping the same number and thread.</li>
          <li>· An outage record and a post-incident review appear on the ticket.</li>
          <li>· The time right now is recorded as the declaration.</li>
        </ul>

        <div className="rounded-lg border p-3">
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={announce}
              onChange={(e) => setAnnounce(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-input"
            />
            <span>
              <span className="flex items-center gap-1.5 font-medium">
                <Megaphone className="h-3.5 w-3.5" /> Tell everyone on the portal
              </span>
              <span className="block text-xs text-muted-foreground">
                Posts a critical announcement. It ends automatically when you stand the incident down.
              </span>
            </span>
          </label>

          {announce && (
            <div className="mt-3 space-y-2 border-t pt-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Headline</label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Seeded from the subject. Customers read this one.
                </p>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">
                  Detail <span className="font-normal">— optional</span>
                </label>
                <RichTextEditor value={body} onChange={setBody} />
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
